import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stack, Label, Dropdown, IDropdownOption, PrimaryButton, DefaultButton, MessageBar, MessageBarType, Spinner } from '@fluentui/react';
import { getUpdateGroupData, updateGroup } from '../../../api/admin/api_admin';
import type { UpdateGroupData } from '../../../api/admin/models_admin';
import { AdminLabel, ErrorDialog, ConfirmDialog } from "../components";
import { isEqual } from 'lodash-es';

export const UpdateGroup: React.FC = () => {
    const { seq } = useParams<{ seq: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<UpdateGroupData | null>(null);
    const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!seq) {
                navigate('/menu/upload');
                return;
            }
            try {
                setLoading(true);
                const result = await getUpdateGroupData(seq);
                if ("error" in result) {
                    setError(result.error);
                } else {
                    setData(result);
                    const codes = result.assignments.map(a => a.group_cd).filter((a): a is number => a !== null);
                    setSelectedKeys(codes);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [seq, navigate]);

    const handleGroupChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            setSelectedKeys(
                option.selected
                    ? [...selectedKeys, option.key as number]
                    : selectedKeys.filter(key => key !== option.key)
            );
        }
    };

    const handleSubmit = async () => {
        if (!seq) return;

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);
            await updateGroup({
                category: seq,
                groupCodes: selectedKeys.map(v => v.toString()),
            });
            setSuccess('グループを更新しました');
            setTimeout(() => navigate('/menu/upload'), 1500);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'グループの更新に失敗しました');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => {
        navigate('/menu/upload');
    };

    if (loading) {
        return (
            <Stack tokens={{ padding: 20 }}>
                <Spinner label="読み込み中..." />
            </Stack>
        );
    }

    if (!data) {
        return (
            <Stack tokens={{ padding: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    データが見つかりません
                </MessageBar>
                <DefaultButton text="戻る" onClick={handleBack} styles={{ root: { marginTop: 20, width: 'fit-content' } }} />
            </Stack>
        );
    }

    const groupOptions: IDropdownOption[] = data.group_list.map(g => ({
        key: g.group_cd,
        text: g.GRP_group_name,
    }));

    return (
        <Stack tokens={{ padding: 20, childrenGap: 20 }}>
            <AdminLabel text='グループ変更' />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            {
                <ConfirmDialog 
                    content={success ?? ""} 
                    onDismiss={() => setSuccess(null)} 
                    isOpen={Boolean(success)}
                    title="確認" />
            }

            <Stack tokens={{ childrenGap: 10 }}>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Label styles={{ root: { width: 150, fontWeight: 600 } }}>カテゴリ名</Label>
                    <Label>{data.container_name}</Label>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Label styles={{ root: { width: 150, fontWeight: 600 } }}>グループCD</Label>
                    <Dropdown
                        placeholder="グループCDを選択"
                        multiSelect
                        options={groupOptions}
                        selectedKeys={selectedKeys}
                        onChange={handleGroupChange}
                        styles={{ root: { flex: 1, maxWidth: 600 } }}
                    />
                </Stack>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text="登録" onClick={handleSubmit} disabled={submitting || isEqual(new Set(selectedKeys), new Set(data.assignments.map(v => v.group_cd)))} />
                <DefaultButton text="戻る" onClick={handleBack} disabled={submitting} />
            </Stack>
        </Stack>
    );
};